<section class="insight-translation card card-body">
  <div class="d-flex justify-content-between align-items-start gap-2 mb-3">
    <div>
      <h5 class="mb-1">Send for translation</h5>
      <p class="small text-muted mb-0">
        Insight {{ insight().id }} — {{ insight().slug }}.
      </p>
    </div>
    <button class="btn btn-sm btn-outline-secondary" type="button" (click)="onClose()">Close</button>
  </div>

  <div class="row g-3">
    <div class="col-12 col-lg-4">
      <label class="form-label" [attr.for]="'source-lang-' + insight().id">Source language</label>
      <!--
        The current value lives on the options, not on the select: a `[value]` binding on the
        select is applied before `@for` has created any option, so the browser has nothing to
        select and the binding is never reapplied (its value has not changed).
      -->
      <select
        class="form-select"
        [id]="'source-lang-' + insight().id"
        (change)="onSourceLangChange($any($event.target).value)"
        [disabled]="isAnythingLoading()"
      >
        @for (locale of supportedLocales; track locale) {
          <option [value]="locale" [selected]="locale === sourceLang()">{{ locale }}</option>
        }
      </select>
      @if (defaultSourceLang() !== 'en') {
        <p class="form-text mb-0">
          This insight holds no English text, so <span class="text-uppercase">{{ defaultSourceLang() }}</span> was
          chosen instead of the usual master language.
        </p>
      }
    </div>

    <div class="col-12 col-lg-8">
      <span class="form-label d-block">Target languages</span>
      <div class="d-flex flex-wrap gap-3">
        @for (locale of availableTargetLocales(); track locale) {
          <div class="form-check">
            <input
              class="form-check-input"
              type="checkbox"
              [id]="'target-' + insight().id + '-' + locale"
              [checked]="isTargetLangSelected(locale)"
              (change)="toggleTargetLang(locale)"
              [disabled]="isAnythingLoading()"
            />
            <label class="form-check-label text-uppercase" [attr.for]="'target-' + insight().id + '-' + locale">{{
              locale
            }}</label>
          </div>
        }
      </div>
    </div>

    <div class="col-12 col-lg-4">
      <span class="form-label d-block">Fields</span>
      <div class="d-flex flex-wrap gap-3">
        @for (field of textFields; track field) {
          <div class="form-check">
            <input
              class="form-check-input"
              type="checkbox"
              [id]="'field-' + insight().id + '-' + field"
              [checked]="isFieldSelected(field)"
              (change)="toggleField(field)"
              [disabled]="isAnythingLoading()"
            />
            <label class="form-check-label" [attr.for]="'field-' + insight().id + '-' + field"
              >{{ fieldLabels[field] }} ({{ field }})</label
            >
          </div>
        }
      </div>
      @if (!selectedFields().length) {
        <p class="form-text mb-0">None selected: every field with content in the source locale.</p>
      }
    </div>

    <div class="col-12 col-lg-8">
      <label class="form-label" [attr.for]="'context-' + insight().id">Context for the provider (optional)</label>
      <input
        class="form-control"
        type="text"
        [id]="'context-' + insight().id"
        [attr.maxlength]="maxContextLength"
        [value]="context()"
        (input)="onContextChange($any($event.target).value)"
        [disabled]="isAnythingLoading()"
        placeholder="e.g. Cultural differences relating to patience while waiting for action, or passivity and compliance contrasted with initiative, activity and self-determination"
      />
      <p class="form-text mb-0">A domain hint. It is not translated. {{ context().length }}/{{ maxContextLength }}</p>
    </div>
  </div>

  <hr />

  <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
    <div class="form-check">
      <input
        class="form-check-input"
        type="checkbox"
        [id]="'save-' + insight().id"
        [checked]="saveToInsight()"
        (change)="onSaveToInsightChange($any($event.target).checked)"
        [disabled]="isAnythingLoading()"
      />
      <label class="form-check-label" [attr.for]="'save-' + insight().id">
        Save the translation to the insight
        <span class="d-block form-text mb-0">
          Off: dry run — nothing is written, but the provider is still called and still billed. On: the translation
          overwrites the insight's text in each target locale.
        </span>
      </label>
    </div>

    <button
      class="btn"
      [class.btn-primary]="!saveToInsight()"
      [class.btn-danger]="saveToInsight()"
      type="button"
      (click)="onSubmit()"
      [disabled]="isAnythingLoading()"
    >
      {{ saveToInsight() ? 'Translate and save' : 'Translate (dry run)' }}
    </button>
  </div>

  @if (validationMessage()) {
    <div class="alert alert-warning mt-3 mb-0">{{ validationMessage() }}</div>
  }

  @if (awaitingCommitConfirmation()) {
    <div class="alert alert-danger mt-3 mb-0">
      <p class="mb-2">
        This will overwrite insight {{ insight().id }} in
        <strong class="text-uppercase">{{ effectiveTargetLangs().join(', ') }}</strong
        >. It cannot be undone from here.
      </p>
      <div class="d-flex gap-2">
        <button class="btn btn-sm btn-danger" type="button" (click)="onSubmit()">Yes, overwrite</button>
        <button class="btn btn-sm btn-outline-secondary" type="button" (click)="cancelCommitConfirmation()">
          Cancel
        </button>
      </div>
    </div>
  }

  @if (isAnythingLoading()) {
    <div class="alert alert-secondary mt-3 mb-0">
      Translating… {{ elapsedSeconds() }}s elapsed. One provider round-trip per field × language, run one after another
      — this can take minutes. Leave the page open.
    </div>
  }

  @if (runError(); as error) {
    <div class="alert alert-danger mt-3 mb-0">
      <p class="mb-0">{{ error.message }}</p>
      @if (error.errors) {
        <ul class="mb-0 mt-2 small">
          @for (entry of error.errors | keyvalue; track entry.key) {
            <li>{{ entry.key }}: {{ entry.value.join(' ') }}</li>
          }
        </ul>
      }
    </div>
  }

  @if (runResponse(); as response) {
    <div class="mt-3">
      <div class="d-flex flex-wrap gap-2 align-items-center mb-2">
        <span
          class="badge rounded-pill"
          [class.text-bg-secondary]="response.dry_run"
          [class.text-bg-success]="!response.dry_run"
        >
          {{ response.dry_run ? 'Dry run — nothing saved' : 'Saved to the insight' }}
        </span>
        <span class="badge rounded-pill text-bg-light">{{ response.results.length }} results</span>
        <span class="badge rounded-pill text-bg-light">{{ totalBilledCharacters() | number }} billed characters</span>
        @if (failedResultCount()) {
          <span class="badge rounded-pill text-bg-danger">{{ failedResultCount() }} failed</span>
        }
      </div>

      @if (response.message) {
        <div class="alert alert-warning">{{ response.message }}</div>
      }

      @for (result of results(); track result.key) {
        <div class="insight-translation__result">
          <h6 class="mb-2">
            {{ result.fieldLabel }} ({{ result.field }}) → <span class="text-uppercase">{{ result.targetLang }}</span>
          </h6>
          <div class="d-flex flex-wrap gap-2 align-items-center mb-1">
            <span class="badge rounded-pill text-bg-light">{{ result.contentFormat }}</span>
            <span class="badge rounded-pill text-bg-light">{{ result.sourceChars | number }} source chars</span>
            @if (result.billedCharacters !== null) {
              <span class="badge rounded-pill text-bg-light">{{ result.billedCharacters | number }} billed</span>
            }
            @if (result.durationMs !== null) {
              <span class="badge rounded-pill text-bg-light">{{ result.durationMs | number }} ms</span>
            }
            @if (result.modelTypeUsed) {
              <span class="badge rounded-pill text-bg-light">{{ result.modelTypeUsed }}</span>
            }
            @if (result.committed) {
              <span class="badge rounded-pill text-bg-success">saved</span>
            }
          </div>

          @if (result.error) {
            <p class="alert alert-danger py-2 mb-0 small">{{ result.error }}</p>
          } @else {
            <!-- Deliberately unrendered: Markdown is shown as stored, so any markup damage is visible. -->
            <pre
              class="insight-translation__text"
              [class.insight-translation__text--collapsed]="!isResultExpanded(result.key)"
              >{{ result.text }}</pre
            >
            <button class="btn btn-sm btn-link p-0" type="button" (click)="toggleResultExpanded(result.key)">
              {{ isResultExpanded(result.key) ? 'Show less' : 'Show all' }}
            </button>
          }
        </div>
      }
    </div>
  }
</section>
